/**
 * @fileoverview Manages hover, select, and modify interactions for tactical annotation tool.
 */

import type { Feature, Map as OlMap } from 'ol';
import { Collection } from 'ol';
import { click, pointerMove } from 'ol/events/condition';
import type { Geometry } from 'ol/geom';
import { Modify } from 'ol/interaction';
import Select from 'ol/interaction/Select';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';

import { getHandleModifyPixelTolerance } from '#nora-ui/map/drawtools/draw-tool-utils';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';

/**
 * Manages OpenLayers interactions for tactical annotation tool.
 */
export class TacticalAnnotationInteractionManager {
  readonly hoverInteraction: Select;
  readonly modifyFeatures: Collection<Feature<Geometry>>;
  readonly modifyInteraction: Modify;
  readonly selectInteraction: Select;
  readonly selectableFeatures: Collection<Feature<Geometry>>;

  private isDragging = false;

  constructor(
    private readonly editingVectorLayer: VectorLayer<VectorSource<Feature<Geometry>>>,
  ) {
    // Create a collection for selectable features that will be populated later
    this.selectableFeatures = new Collection<Feature<Geometry>>();

    // Hover interaction
    this.hoverInteraction = new Select({
      condition: event => pointerMove(event) && !this.isDragging,
      hitTolerance: 15,
      features: this.selectableFeatures,
      layers: [this.editingVectorLayer],
      style: null,
      filter: feature =>
        feature.get('preventSelection') !== true
        && feature.get('isCornerHandle') !== true
        && feature.get('isPointIndicator') !== true,
    });

    // Click selection interaction
    this.selectInteraction = new Select({
      condition: click,
      hitTolerance: 15,
      style: getFeatureStyle,
      layers: [this.editingVectorLayer],
      filter: feature =>
        feature.get('preventSelection') !== true
        && feature.get('isPointIndicator') !== true,
    });

    // Modify interaction for dragging
    this.modifyFeatures = new Collection<Feature<Geometry>>();
    this.modifyInteraction = new Modify({
      features: this.modifyFeatures,
      insertVertexCondition: () => false,
      deleteCondition: () => false,
      style: () => [],
      pixelTolerance: getHandleModifyPixelTolerance(),
    });
  }

  /**
   * Adds all interactions to the map.
   */
  activate(map: OlMap): void {
    map.addInteraction(this.selectInteraction);
    map.addInteraction(this.hoverInteraction);
    map.addInteraction(this.modifyInteraction);
  }

  /**
   * Removes all interactions from the map.
   */
  deactivate(map: OlMap): void {
    map.removeInteraction(this.hoverInteraction);
    map.removeInteraction(this.selectInteraction);
    map.removeInteraction(this.modifyInteraction);
  }

  /**
   * Checks if currently dragging.
   */
  getIsDragging(): boolean {
    return this.isDragging;
  }

  /**
   * Sets the dragging state (disables hover during drag).
   */
  setDragging(dragging: boolean, map: OlMap): void {
    this.isDragging = dragging;

    if (dragging) {
      map.removeInteraction(this.hoverInteraction);
    }
    else {
      map.addInteraction(this.hoverInteraction);
    }
  }
}
